import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Download, Sparkles, Loader2, ListOrdered, Film, Volume2, Share2, Camera } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { Brand, brand } from "../../context/CompanyContext";

/**
 * AdminTutorials — onboarding guides with PT-PT narrated VIDEO.
 * "Gerar (slides)" produces an MP3 + slide-based MP4. "Gerar (screen-rec)"
 * runs a real Playwright walkthrough recording of the live app, overlaid
 * with subtitles + click ripples + zoom spotlights. The latter takes 2-3
 * minutes and is polled for progress.
 */
export default function AdminTutorials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [screenJob, setScreenJob] = useState({});
  const pollRef = useRef({});

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tutorials");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  useEffect(() => () => {
    Object.values(pollRef.current).forEach((id) => clearInterval(id));
  }, []);

  async function generate(tid) {
    setGenerating(tid);
    try {
      await api.post(`/admin/tutorials/${tid}/generate`, null, { timeout: 180000 });
      toast.success("Vídeo e áudio gerados");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setGenerating(null);
  }

  async function startScreenRec(tid) {
    if (screenJob[tid]?.status === "running") return;
    setScreenJob((s) => ({ ...s, [tid]: { status: "running", progress: 0, step: "queued" } }));
    try {
      await api.post(`/admin/tutorials/${tid}/generate-screen`);
      toast.success("Gravação de ecrã iniciada — acompanhe a barra de progresso");
      pollRef.current[tid] = setInterval(async () => {
        try {
          const { data } = await api.get(`/admin/tutorials/${tid}/job`);
          setScreenJob((s) => ({ ...s, [tid]: data }));
          if (data.status === "done" || data.status === "error" || data.status === "idle") {
            clearInterval(pollRef.current[tid]);
            delete pollRef.current[tid];
            if (data.status === "done") {
              toast.success("Gravação de ecrã concluída — recarregando tutorial");
              await load();
            } else if (data.status === "error") {
              toast.error(`Falha: ${data.message || "erro desconhecido"}`);
            }
          }
        } catch {
          // transient — keep polling
        }
      }, 2500);
    } catch (e) {
      toast.error(formatApiError(e));
      setScreenJob((s) => ({ ...s, [tid]: { status: "error", message: formatApiError(e) } }));
    }
  }

  function mediaUrl(tid, kind) {
    return `${BACKEND_URL}/api/admin/tutorials/${tid}/${kind}`;
  }

  function publicVideoUrl(tid) {
    return `${BACKEND_URL}/api/tutorials/${tid}/video.mp4`;
  }

  function shareWhatsApp(tid, title) {
    const url = publicVideoUrl(tid);
    const text = `${brand()} · tutorial: ${title}\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  }

  async function downloadMedia(tid, title, kind, ext) {
    try {
      const res = await api.get(`/admin/tutorials/${tid}/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `luxstay-${tid}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${title} (${ext.toUpperCase()}) descarregado`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-8" data-testid="admin-tutorials-page">
      <header>
        <div className="eyebrow text-[#d4af37] flex items-center gap-2">
          <Film size={14} /> Admin · Tutoriais em vídeo
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Vídeo guia do cliente</h1>
        <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl leading-relaxed">
          Um único vídeo em Full HD (1920×1080) com narração feminina em português
          de Portugal que percorre toda a experiência do cliente: criar conta,
          agendar limpeza, pagamento, relatório, avaliações e indicações. Veja aqui
          mesmo, descarregue o MP4 para o YouTube ou partilhe por WhatsApp.
        </p>
      </header>

      {loading ? (
        <div className="text-sm text-[#a0b2af]">A carregar…</div>
      ) : (
        <div className="space-y-5">
          {items.map((t, i) => (
            <article key={t.id} className="lux-card p-5 md:p-6"
                     data-testid={`tutorial-${t.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-sm bg-[#d4af37]/10 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] font-display text-lg shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl md:text-2xl text-[#fdfbf7]">
                    {t.title}
                  </div>
                  <p className="text-sm text-[#a0b2af] mt-1">{t.short}</p>

                  <div className="mt-4">
                    <div className="eyebrow text-[10px] text-[#a0b2af] flex items-center gap-1.5 mb-2">
                      <ListOrdered size={12} /> Passos
                    </div>
                    <ol className="space-y-1.5 list-decimal pl-5 text-sm text-[#fdfbf7]">
                      {t.steps.map((s, si) => (
                        <li key={si} className="leading-relaxed">{s}</li>
                      ))}
                    </ol>
                  </div>

                  <div className="mt-5 flex flex-wrap items-center gap-2">
                    {!t.has_video ? (
                      <button onClick={() => generate(t.id)}
                              disabled={generating === t.id}
                              className="btn-gold px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60"
                              data-testid={`tutorial-generate-${t.id}`}>
                        {generating === t.id ? (
                          <><Loader2 size={14} className="animate-spin" /> A gerar vídeo…</>
                        ) : (
                          <><Sparkles size={14} /> Gerar vídeo PT-PT</>
                        )}
                      </button>
                    ) : (
                      <>
                        <button onClick={() => setPlaying(playing === t.id ? null : t.id)}
                                className="px-4 py-2 text-xs uppercase tracking-widest border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37]/10 inline-flex items-center gap-2"
                                data-testid={`tutorial-play-${t.id}`}>
                          <Play size={14} /> {playing === t.id ? "Fechar vídeo" : "Ver vídeo"}
                        </button>
                        <button onClick={() => downloadMedia(t.id, t.title, "video", "mp4")}
                                className="px-4 py-2 text-xs uppercase tracking-widest border border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors inline-flex items-center gap-2"
                                data-testid={`tutorial-download-video-${t.id}`}>
                          <Download size={14} /> MP4
                        </button>
                        <button onClick={() => downloadMedia(t.id, t.title, "audio", "mp3")}
                                className="px-4 py-2 text-xs uppercase tracking-widest border border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] transition-colors inline-flex items-center gap-2"
                                data-testid={`tutorial-download-audio-${t.id}`}>
                          <Volume2 size={14} /> MP3
                        </button>
                        <button onClick={() => shareWhatsApp(t.id, t.title)}
                                className="px-4 py-2 text-xs uppercase tracking-widest border border-[#10b981]/50 text-[#10b981] hover:bg-[#10b981]/10 transition-colors inline-flex items-center gap-2"
                                title="Partilha o link público do MP4 no WhatsApp"
                                data-testid={`tutorial-whatsapp-${t.id}`}>
                          <Share2 size={14} /> WhatsApp
                        </button>
                        <button onClick={() => generate(t.id)}
                                disabled={generating === t.id}
                                className="px-3 py-2 text-[11px] uppercase tracking-widest text-[#a0b2af] hover:text-[#fdfbf7] disabled:opacity-60"
                                data-testid={`tutorial-regenerate-${t.id}`}>
                          {generating === t.id ? "A regenerar…" : "Regenerar slides"}
                        </button>
                      </>
                    )}
                    <button onClick={() => startScreenRec(t.id)}
                            disabled={screenJob[t.id]?.status === "running"}
                            className="px-3 py-2 text-[11px] uppercase tracking-widest border border-[#3b82f6]/50 text-[#93c5fd] hover:bg-[#3b82f6]/10 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
                            title="Grava o app em funcionamento (Playwright) · 2-3 min"
                            data-testid={`tutorial-screenrec-${t.id}`}>
                      <Camera size={13} />
                      {screenJob[t.id]?.status === "running"
                        ? `Gravando… ${screenJob[t.id]?.progress || 0}%`
                        : "Gravar ecrã"}
                    </button>
                  </div>

                  {screenJob[t.id]?.status === "running" && (
                    <div className="mt-3" data-testid={`tutorial-screenrec-progress-${t.id}`}>
                      <div className="h-1.5 bg-[#1a4640] rounded-sm overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#3b82f6] to-[#d4af37] transition-all duration-500"
                             style={{ width: `${screenJob[t.id]?.progress || 0}%` }} />
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-[#93c5fd] mt-1.5">
                        {{
                          queued: "Na fila",
                          audio: "A gerar áudio…",
                          recording: "A gravar o ecrã com Playwright…",
                          compose: "A montar o MP4 final…",
                          finalize: "A finalizar…",
                          done: "Concluído",
                        }[screenJob[t.id]?.step] || screenJob[t.id]?.step}
                      </div>
                    </div>
                  )}

                  {playing === t.id && t.has_video && (
                    <video
                      autoPlay
                      controls
                      playsInline
                      preload="auto"
                      src={publicVideoUrl(t.id)}
                      className="w-full mt-4 rounded-sm border border-[#1a4640] bg-black"
                      data-testid={`tutorial-video-${t.id}`}
                    />
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <div className="text-[11px] text-[#a0b2af] border-t border-[#1a4640] pt-4">
        O vídeo é composto automaticamente: narração IA (voz feminina "nova" em
        PT-PT) sobreposta a slides com a identidade <Brand short />. Fica em cache por
        guião — só volta a ser gerado se o texto for atualizado. Formato
        1920×1080 Full HD H.264 (YouTube-ready).
      </div>
    </div>
  );
}
